import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@app/../environments/environment';
import { ContactDepartment, ContactInquiryRequest, ContactPerson } from '@app/model/contact.model';
import { ContactDataService } from '@app/services/contact-data.service';
import { TranslationService } from '@app/core/_base/layout';
import { AppState } from '@app/core/reducers';
import { currentUser } from '@app/core/auth';
import { User } from '@app/core/auth/_models/user.model';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'meu-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss']
})
export class ContactComponent implements OnInit, OnDestroy {
  host = environment.backendhost;

  departments: ContactDepartment[] = [];
  /** Code of the active department (tab). `''` = all. */
  activeDepartmentCode = '';
  persons: ContactPerson[] = [];
  totalPersons = 0;
  page = 1;
  pageSize = 12;
  loadingDepartments = false;
  loadingPersons = false;

  /** Persons used for the contact form dropdown (loaded separately for the selected department). */
  formPersons: ContactPerson[] = [];
  loadingFormPersons = false;

  /** Multi-field search keyword. */
  searchTerm = '';
  private searchSubject = new Subject<string>();

  /** Person whose detail is being viewed (click to expand). */
  expandedPersonId: string | null = null;

  /** Contact form state. */
  inquiryForm: FormGroup;
  submittingInquiry = false;
  inquirySuccess = false;
  inquiryError: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private contactDataService: ContactDataService,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
    private translate: TranslateService
  ) {
    this.inquiryForm = this.buildInquiryForm();
  }

  ngOnInit(): void {
    this.loadDepartments();

    // Listen for search keyword changes (debounce 350ms).
    this.searchSubject
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.page = 1;
        this.loadPersons();
      });

    // Load the initial person list (all departments).
    this.loadPersons();

    // Auto-fill info from the logged-in account (email, full name, phone, company).
    this.prefillFromLoggedInUser();

    // When the user switches language, reload departments & persons to get the matching translations.
    this.translationService.language$
      .pipe(debounceTime(50), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadDepartments();
        this.loadPersons();
        // Reload persons of the department selected in the form (if any).
        const departmentId = this.inquiryForm.get('department_id');
        if (departmentId && departmentId.value) {
          this.loadFormPersons(departmentId.value);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  // ---------------------- Auto-fill from logged-in user ----------------------

  /** Pre-fill email/full name/phone/company from the logged-in account. */
  private prefillFromLoggedInUser(): void {
    this.store
      .pipe(
        select(currentUser),
        filter((user: User | null) => !!user),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((user: User | null) => {
        // The getMyInfo API returns some fields in snake_case (full_name, company_name)
        // unlike the User model (fullname, companyName) — cast to any to access both.
        const u = user as any;
        const patch: Partial<ContactInquiryRequest> = {};

        if (u.email) {
          patch.email = u.email;
        }
        // API returns `full_name` (snake_case), model uses `fullname` (camelCase).
        const fullName = u.full_name || u.fullname || u.extend_user_full_name
          || [u.first_name, u.last_name].filter(Boolean).join(' ');
        if (fullName) {
          patch.full_name = fullName;
        }
        const phone = u.phone || u.cell_phone;
        if (phone) {
          patch.phone = phone;
        }
        // API returns `company_name` (snake_case), model uses `companyName` (camelCase).
        const company = u.company_name || u.companyName;
        if (company) {
          patch.company_name = company;
        }

        if (Object.keys(patch).length) {
          this.inquiryForm.patchValue(patch);
          // Reset control state so validation errors don't show right after prefill.
          Object.keys(patch).forEach(name => {
            const c = this.inquiryForm.get(name);
            if (c) {
              c.markAsUntouched({ onlySelf: true });
              c.markAsPristine({ onlySelf: true });
            }
          });
          this.cdr.detectChanges();
        }
      });
  }

  // ---------------------- Departments / Tabs ----------------------

  loadDepartments(): void {
    this.loadingDepartments = true;
    this.contactDataService
      .getDepartments()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.departments = (data || []).slice().sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
          );
          this.loadingDepartments = false;
        },
        error: () => {
          this.loadingDepartments = false;
        }
      });
  }

  selectDepartment(code: string): void {
    if (this.activeDepartmentCode === code) {
      return;
    }
    this.activeDepartmentCode = code;
    this.page = 1;
    this.searchTerm = '';
    this.expandedPersonId = null;
    this.loadPersons();
  }

  // ---------------------- Persons ----------------------

  loadPersons(): void {
    this.loadingPersons = true;
    const filters = this.buildFilters();
    this.contactDataService
      .getPersons({ filters, page: this.page, pageSize: this.pageSize, sorts: 'sort_order' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.persons = result.collection || [];
          this.totalPersons = result.total || 0;
          this.loadingPersons = false;
        },
        error: () => {
          this.persons = [];
          this.totalPersons = 0;
          this.loadingPersons = false;
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  /** Build the Sieve filters string combining department + multi-field keyword. */
  private buildFilters(): string {
    const conditions: string[] = [];
    if (this.activeDepartmentCode) {
      conditions.push(`departmentCode==${this.activeDepartmentCode}`);
    }
    const keyword = (this.searchTerm || '').trim();
    if (keyword) {
      conditions.push(`departmentCode|name|position|phone|area@=${keyword}`);
    }
    return conditions.join(',');
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalPersons / this.pageSize));
  }

  goToPage(target: number): void {
    if (target < 1 || target > this.totalPages || target === this.page) {
      return;
    }
    this.page = target;
    this.expandedPersonId = null;
    this.loadPersons();
  }

  pageNumbers(): number[] {
    const total = this.totalPages;
    const current = this.page;
    const delta = 2;
    const range: number[] = [];
    const start = Math.max(1, current - delta);
    const end = Math.min(total, current + delta);
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    return range;
  }

  togglePersonDetail(person: ContactPerson): void {
    this.expandedPersonId = this.expandedPersonId === person.id ? null : person.id;
  }

  // ---------------------- Image / contact helpers ----------------------

  /** Returns the full avatar URL (prepends backendhost if it is a relative path). */
  getAvatarUrl(avatar: string | null | undefined): string | null {
    if (!avatar) {
      return null;
    }
    if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
      return avatar;
    }
    return this.host + (avatar.startsWith('/') ? avatar : '/' + avatar);
  }

  /** Returns the full department image URL. */
  getDepartmentImageUrl(image: string | null | undefined): string | null {
    return this.getAvatarUrl(image);
  }

  /** Open a Zalo chat via the Zalo number. */
  openZalo(zalo: string | null | undefined): void {
    if (!zalo) {
      return;
    }
    window.open('https://zalo.me/' + encodeURIComponent(zalo), '_blank');
  }

  // ---------------------- Contact form ----------------------

  private buildInquiryForm(): FormGroup {
    return this.fb.group({
      full_name: ['', [Validators.required, Validators.maxLength(200)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      phone: ['', [Validators.required, Validators.maxLength(15), Validators.pattern(/^[0-9]+$/)]],
      company_name: ['', [Validators.maxLength(200)]],
      department_id: [''],
      person_id: [''],
      subject: ['', [Validators.required, Validators.maxLength(300)]],
      message: ['', [Validators.required, Validators.maxLength(2000)]]
    });
  }

  /** When a department is selected in the form, reset person_id and reload that department's persons. */
  onFormDepartmentChange(): void {
    this.inquiryForm.patchValue({ person_id: '' });
    this.formPersons = [];
    const control = this.inquiryForm.get('department_id');
    const departmentId = control ? control.value : '';
    if (!departmentId) {
      return;
    }
    this.loadFormPersons(departmentId);
  }

  /** Load all persons of a department (used for the form dropdown, not paginated). */
  private loadFormPersons(departmentId: string): void {
    this.loadingFormPersons = true;
    const filters = `departmentId==${departmentId}`;
    this.contactDataService
      .getPersons({ filters, page: 1, pageSize: 1000, sorts: 'sort_order' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.formPersons = result.collection || [];
          this.loadingFormPersons = false;
        },
        error: () => {
          this.formPersons = [];
          this.loadingFormPersons = false;
        }
      });
  }

  /** Persons available to select in the form (for the selected department). */
  get formAvailablePersons(): ContactPerson[] {
    return this.formPersons;
  }

  // ---------------------- Phone validation helpers ----------------------

  onPhoneKeypress(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow digits 0-9 and control keys (backspace, tab, arrow...)
    if (charCode >= 48 && charCode <= 57) {
      return;
    }
    // Allow control keys
    if (charCode === 0 || charCode === 8 || charCode === 9 || charCode === 13) {
      return;
    }
    event.preventDefault();
  }

  getPhoneErrorMessage(): string {
    const control = this.inquiryForm.get('phone');
    if (!control) {
      return 'CONTACT_PAGE.ERR_PHONE_REQUIRED';
    }
    if (control.hasError('required')) {
      return 'CONTACT_PAGE.ERR_PHONE_REQUIRED';
    }
    if (control.hasError('pattern')) {
      return 'CONTACT_PAGE.ERR_PHONE_PATTERN';
    }
    if (control.hasError('maxlength')) {
      return 'CONTACT_PAGE.ERR_PHONE_MAXLENGTH';
    }
    return 'CONTACT_PAGE.ERR_PHONE_INVALID';
  }

  prefillFromPerson(person: ContactPerson): void {
    const departmentId = person.department_id || '';
    this.inquiryForm.patchValue({
      department_id: departmentId,
      person_id: person.id || ''
    });
    // Load the department's persons so the dropdown shows the selected person.
    if (departmentId) {
      this.loadFormPersons(departmentId);
    }
    // Scroll down to the form.
    setTimeout(() => {
      const el = document.getElementById('contact-inquiry-form');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  submitInquiry(): void {
    if (this.inquiryForm.invalid) {
      this.inquiryForm.markAllAsTouched();
      return;
    }
    this.submittingInquiry = true;
    this.inquirySuccess = false;
    this.inquiryError = null;

    const raw = this.inquiryForm.value as ContactInquiryRequest;
    const payload: ContactInquiryRequest = {
      full_name: raw.full_name,
      email: raw.email,
      phone: raw.phone,
      company_name: raw.company_name || null,
      department_id: raw.department_id || null,
      person_id: raw.person_id || null,
      subject: raw.subject,
      message: raw.message
    };

    this.contactDataService
      .submitInquiry(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.submittingInquiry = false;
          if (res && res.success) {
            this.inquirySuccess = true;
            this.inquiryForm.reset();
            this.formPersons = [];
          } else {
            this.inquiryError = (res && res.message) || this.translate.instant('CONTACT_PAGE.ERR_SUBMIT');
          }
        },
        error: (err) => {
          this.submittingInquiry = false;
          this.inquiryError =
            (err && err.error && err.error.message) ||
            this.translate.instant('CONTACT_PAGE.ERR_SUBMIT_GENERIC');
        }
      });
  }

  closeInquiryAlert(): void {
    this.inquirySuccess = false;
    this.inquiryError = null;
  }

  // ---------------------- Form display helpers ----------------------

  isInvalid(controlName: string): boolean {
    const control = this.inquiryForm.get(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }
}
